import * as React from "react"
import { cn } from "@/lib/utils"
import { Icon, type IconName } from "@/components/icon/icon"
import styles from './buttonIcon.module.css'

type ButtonIconSize = 'sm' | 'md' | 'lg' | 'compact'
type ButtonIconVariant = 'default' | 'white'
type ButtonIconShape = 'square' | 'circle'

const buttonIconVariants = ({
  size = 'md',
  variant = 'default',
  shape = 'square',
  border = false,
  className,
}: {
  size?: ButtonIconSize
  variant?: ButtonIconVariant
  shape?: ButtonIconShape
  border?: boolean
  className?: string
}) =>
  cn(
    styles.root,
    variant === 'white' && styles.white,
    shape === 'circle' && styles.circle,
    size === 'sm' && styles.sm,
    size === 'md' && styles.md,
    size === 'lg' && styles.lg,
    size === 'compact' && styles.compact,
    border ? styles.border : styles.noBorder,
    className,
  )

export interface ButtonIconProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  name?: IconName
  size?: ButtonIconSize
  variant?: ButtonIconVariant
  shape?: ButtonIconShape
  border?: boolean
}

const ButtonIcon = React.forwardRef<HTMLButtonElement, ButtonIconProps>(
  ({ className, size, variant, shape, border, name = "X", children, ...props }, ref) => {
    // Icon box: 16px for small, 24px for md and lg
    const iconSizePx = size === "sm" || size === 'compact' ? 16 : 24
    return (
      <button
        ref={ref}
        className={buttonIconVariants({ size, variant, shape, border, className })}
        type={props.type || "button"}
        {...props}
      >
        {children ? (
          children
        ) : (
          <Icon name={name} size={iconSizePx} strokeWidth={1.5} absoluteStrokeWidth />
        )}
      </button>
    )
  }
)
ButtonIcon.displayName = "ButtonIcon"

export { ButtonIcon, buttonIconVariants }
export type { ButtonIconSize, ButtonIconVariant, ButtonIconShape }
